import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFormStyles } from "../../../hooks/useFormStyles";
import {
  getInterventionFormSchema,
  InterventionForm as InterventionFormType,
} from "../../../types/forms/capture";
import { Intervention } from "../../../types/model/intervention";

interface InterventionFormProps {
  interventionData?: Intervention;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: InterventionFormType) => Promise<void>;
}

export default function InterventionForm({
  interventionData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<InterventionFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getInterventionFormSchema(t);
  const methods = useForm<InterventionFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      performedAt: interventionData?.performedAt ?? null,
      performedBy: interventionData?.performedBy ?? undefined,
      notes: interventionData?.notes ?? null,
    },
  });

  const handleFormSubmit = async (values: InterventionFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="performedAt"
                control={methods.control}
                render={({ field }) => {
                  const value = field.value
                    ? dayjs(field.value, "YYYY-MM-DD HH:mm:ss")
                    : null;

                  return (
                    <DateTimePicker
                      label={t("captures.capture.intervention.performed-at")}
                      value={value}
                      format="DD/MM/YYYY, HH:mm"
                      onChange={(date) => {
                        if (date === null) {
                          field.onChange(null);
                          return;
                        }
                        field.onChange(date.format("YYYY-MM-DD HH:mm:ss"));
                      }}
                      disabled={!isEditing || isSaving || isDeleting}
                      sx={{ ...formStyles }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: isEditing ? "outlined" : "standard",
                          error: !!methods.formState.errors.performedAt,
                          helperText:
                            methods.formState.errors.performedAt?.message,
                        },
                      }}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={`${t("captures.capture.intervention.performed-by")} *`}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("performedBy")}
              error={!!methods.formState.errors.performedBy}
              helperText={methods.formState.errors.performedBy?.message}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              label={t("captures.capture.intervention.notes")}
              placeholder={t("captures.capture.intervention.notes-placeholder")}
              multiline
              minRows={6}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("notes")}
              error={!!methods.formState.errors.notes}
              helperText={methods.formState.errors.notes?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
